import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import ShoppingBagIcon from "@redotech/redo-icons/shopping-bag-01.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Provider } from "@redotech/redo-order-common/provider";
import type {
  ProductInfo,
  VariantInfo,
} from "@redotech/redo-product-common/product";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import {
  linkFloatingToolbarFieldClassName,
  LinkFloatingToolbarRow,
  type LinkFloatingToolbarInsertExtensionProps,
} from "@redotech/redo-web/components/plate-ui/link-floating-toolbar";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { getProductLinkSelection } from "./product-link-selection";

/** Shares the popover's field styling so results match the search box exactly. */
const ProductResultButton = memo(function ProductResultButton({
  onClick,
  text,
}: {
  onClick: () => void;
  text: string;
}) {
  return (
    <button
      className={`${linkFloatingToolbarFieldClassName} items-center truncate text-left hover:bg-accent hover:text-accent-foreground`}
      onClick={onClick}
      onMouseDown={(event) => event.preventDefault()}
      type="button"
    >
      {text}
    </button>
  );
});

export const ProductLinkPicker = memo(function ProductLinkPicker({
  setLink,
  expanded,
  setExpanded,
}: LinkFloatingToolbarInsertExtensionProps) {
  if (!expanded) {
    return (
      <LinkFloatingToolbarRow Icon={ShoppingBagIcon}>
        <button
          className={`${linkFloatingToolbarFieldClassName} items-center text-left`}
          onClick={() => setExpanded(true)}
          onMouseDown={(event) => event.preventDefault()}
          type="button"
        >
          Choose product
        </button>
        <Flex align="center" className="pr-2 text-muted-foreground">
          <RedoIcon Icon={ChevronRightIcon} size={16} />
        </Flex>
      </LinkFloatingToolbarRow>
    );
  }
  return (
    <ProductSearch collapse={() => setExpanded(false)} setLink={setLink} />
  );
});

/** Mounted only while expanded, so the search resets every time it opens. */
const ProductSearch = memo(function ProductSearch({
  collapse,
  setLink,
}: {
  collapse: () => void;
  setLink: LinkFloatingToolbarInsertExtensionProps["setLink"];
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 250);
  const productsLoad = useLoad<readonly ProductInfo[]>(
    async (signal) => {
      if (!debouncedQuery.trim()) {
        return [];
      }
      const { products } = await rpcClient.searchProducts(
        { searchString: debouncedQuery, provider: Provider.SHOPIFY },
        { signal },
      );
      return products;
    },
    [debouncedQuery, rpcClient],
  );

  const selectProduct = (product: ProductInfo, variant?: VariantInfo) => {
    const selection = getProductLinkSelection({
      product,
      storeUrl: team.storeUrl,
      variant,
    });
    if (!selection) {
      return;
    }
    setLink(selection);
    collapse();
  };

  return (
    <Flex className="min-h-0 flex-1" dir="column" gap="xs">
      <Flex align="center" shrink={0}>
        <button
          aria-label="Back to link fields"
          className="flex items-center pr-1 pl-2 text-muted-foreground"
          onClick={collapse}
          onMouseDown={(event) => event.preventDefault()}
          type="button"
        >
          <RedoIcon Icon={ArrowLeftIcon} size={16} />
        </button>
        <input
          autoFocus
          className={linkFloatingToolbarFieldClassName}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search products"
          value={query}
        />
      </Flex>
      <Flex className="min-h-0 flex-1 overflow-y-auto" dir="column">
        <Flex dir="column" gap="xs">
          {productsLoad.pending ? (
            <Text fontSize="xs" p="sm" textColor="secondary">
              Searching products...
            </Text>
          ) : productsLoad.error ? (
            <Text fontSize="xs" p="sm" textColor="secondary">
              Could not search products
            </Text>
          ) : query.trim() && productsLoad.value?.length === 0 ? (
            <Text fontSize="xs" p="sm" textColor="secondary">
              No matching products
            </Text>
          ) : (
            productsLoad.value?.flatMap((product) =>
              product.variants.length > 0
                ? product.variants.map((variant) => (
                    <ProductResultButton
                      key={variant.id}
                      onClick={() => selectProduct(product, variant)}
                      text={`${product.name} — ${variant.name}`}
                    />
                  ))
                : [
                    <ProductResultButton
                      key={product.id}
                      onClick={() => selectProduct(product)}
                      text={product.name}
                    />,
                  ],
            )
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});
